import { ErrorBoundary, GlobalHeader, postClose } from "@WahooFitness/wahoo-offline-mfe";
import Box from "@mui/material/Box";
import React, { useCallback, useEffect } from "react";
import { Trans } from "@lingui/macro";
import usePairedDevice from "@/hooks/usePairedDevice";
import { useBCFirstRunState } from "@/hooks/useBCFirstRunState";
import { WSMBoltAppFirstRunState } from "@WahooFitness/wsm-native/dist/esm/types/bolt_cfg";
import { WfIconNavigationCloseRegular } from "@WahooFitness/redesignr";
import { Typography, Container } from "@mui/material";
import { useNavigate } from "react-router";
import { useWsmPlugin } from "@/hooks/useWsmPlugin";
import PasscodeImage from "@/assets/ElemntPasscodeImage.png";

interface PasscodeOnboardingProps {
  appToken: string;
}

const PasscodeOnboarding: React.FC<PasscodeOnboardingProps> = ({ appToken }) => {
  const { isDeviceConnected, deviceError, reloadDevice } = usePairedDevice(appToken);
  const navigate = useNavigate();

  const { sensorManagerPlugin } = useWsmPlugin();

  const { setStateTransition, firstRunState, reloadFirstRunState } = useBCFirstRunState(appToken);
  useEffect(() => {
    setStateTransition({
      desiredState: WSMBoltAppFirstRunState.OnDeviceSetupPasscode,
      allowedStates: [WSMBoltAppFirstRunState.Complete],
    });
  }, [setStateTransition]);

  const closeOnboarding = useCallback(() => {
    postClose("onboarding");
  }, []);

  useEffect(() => {
    if (firstRunState?.state === WSMBoltAppFirstRunState.Complete) {
      navigate("../complete");
    }
  }, [firstRunState, navigate]);

  useEffect(() => {
    const firstRunListener = sensorManagerPlugin.addListener("firstRunStateChange", (event) => {
      if (event.appToken === appToken) {
        reloadFirstRunState();
      }
    });
    return () => {
      (async () => (await firstRunListener).remove())();
    };
  }, [appToken, reloadFirstRunState, sensorManagerPlugin]);

  return (
    <>
      <GlobalHeader
        primaryRightActionItem={{
          icon: <WfIconNavigationCloseRegular />,
          action: closeOnboarding,
        }}
      />
      <Container maxWidth="sm" disableGutters sx={{ minHeight: "100%" }}>
        <Box
          display="flex"
          flexDirection="column"
          width="100%"
          alignItems="center"
          justifyContent="space-between"
          height="100%"
        >
          <ErrorBoundary isLoading={!isDeviceConnected} error={deviceError} onReload={reloadDevice}>
            <Box width="100%" display="flex" flexDirection="column" alignItems="center" px={2}>
              <Box
                height={284}
                width={200}
                display="flex"
                justifyContent="center"
                alignItems="center"
                py={2} // Add padding to top and bottom
              >
                <img
                  src={PasscodeImage}
                  alt="ELEMNT device passcode setup illustration"
                  style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                />
              </Box>
              <Box
                width="100%"
                display="flex"
                alignItems="center"
                flexDirection="column"
                py={2}
                gap={1}
              >
                <Typography variant="prose-lg-bold" textAlign="center">
                  <Trans>Set an optional passcode</Trans>
                </Typography>
                <Typography variant="prose-sm" textAlign="center">
                  <Trans>
                    Follow the on-screen instructions on your ELEMNT to set a 6-digit passcode.
                  </Trans>
                </Typography>
              </Box>
            </Box>
          </ErrorBoundary>
        </Box>
      </Container>
    </>
  );
};

export { PasscodeOnboarding };
